import { format } from 'date-fns';
import React, { forwardRef } from 'react';
import { toast } from 'react-toastify';
import { useContextSelector } from 'use-context-selector';
import Button from '../../../../components/Button';
import { Unit } from '../../../../components/ServiceItem';
import { CartContext } from '../../../../contexts/CartContext';
import { ItemToSchedule, ModuleContext } from '../../../../contexts/ModuleContext';
import { PatientProps } from '../../../../contexts/PatientContext';
import { RouterContext } from '../../../../contexts/RouterContext';
import { ScheduleContext } from '../../../../contexts/ScheduleContext';
import { useSchedulingMount } from '../../../../hooks/useSchedulingMount';
import { CoverageUnitSchedule } from '../../../../hooks/useSelectCoverage';
import { CoverageProps, ProductItemProps } from '../../../../models/Product';
import { EavSlotsProps, ScheduleItemProps, typeLocationSchedule } from '../../../../models/Schedule';
import { ScheduleSpecialist, ScheduleSpecialistBuilder } from '../../../../models/ScheduleSpecialist';
import { Specialist, SpecialistUnit } from '../../../../models/Specialist';
import { UnitAppointment } from '../../../../models/UnitSchedule';
import {
  APPOINTMENT_TYPE,
  ATTENDANCE_TYPE,
  checkSalesForceSlots,
  fetchSlotsSpecialistDates
} from '../../../../modules/slots';
import { PAGES } from '../../../../Router/pages';
import { getRangeDateFromToday } from '../../../../utils/dates';
import { ScheduleChannelsMapped } from '../../../../utils/enums/channels';
import { gtmEvent } from '../../../../utils/gtm';
import { handleSpecialtiesWithExpertises } from '../../../../utils/specialist-utils';
import { PatientScheduleProps } from '../../../Configurator';

type ActionButtonProps = {
  selectedItemToSchedule: ItemToSchedule;
  specialistUnitSelected: SpecialistUnit | undefined;
  selectedUnit: Unit | undefined;
  eavLocation: any;
  setLoadingChangeProduct: (value: boolean) => void;
  coverageUnit: CoverageUnitSchedule | undefined;
  findProduct: (productId: string) => ProductItemProps | undefined;
  selectedCoverage: CoverageProps | undefined;
  coveragesSpecialist: {
    presential: SpecialistUnit[];
    telemedicine: SpecialistUnit[];
  };
  selectedCoverageSpecialist: { presencial: boolean; telemedicine: boolean };
  selectedSpecialist: Specialist | undefined;

  setEavSlotsData: (eavSlots: EavSlotsProps) => void;
  getNotScheduledProducts: ({
    patient,
    productList
  }: {
    patient: PatientProps;
    productList: ProductItemProps[];
  }) => PatientScheduleProps[];
  scheduleSpecialistSelected: ScheduleSpecialist;
  changeScheduleSpecialist: (schedule: ScheduleSpecialist) => void;
  changeSchedule: (schedule: ScheduleItemProps) => ScheduleItemProps;
  selectedItem: ScheduleItemProps;
  patient?: PatientProps;
};

export const ActionButton = forwardRef(
  (
    {
      selectedItemToSchedule,
      specialistUnitSelected,
      selectedUnit,
      eavLocation,
      setLoadingChangeProduct,
      coverageUnit,
      // patientList,
      findProduct,
      // findPatiendById,
      selectedCoverage,
      coveragesSpecialist,
      selectedSpecialist,
      selectedCoverageSpecialist,

      setEavSlotsData,
      getNotScheduledProducts,
      scheduleSpecialistSelected,
      changeScheduleSpecialist,
      changeSchedule,
      selectedItem,
      patient
    }: ActionButtonProps,
    ref
  ) => {
    const { scheduleItems } = useContextSelector(ScheduleContext, state => state);
    const { selectedService } = useContextSelector(ModuleContext, state => state);
    const { productList } = useContextSelector(CartContext, state => state);
    const { goTo } = useContextSelector(RouterContext, state => state);
    const useScheduling = useSchedulingMount({ changeSchedule, selectedSchedule: selectedItem });

    const verifyAllowNextStep = (careType: string | undefined) => {
      if (selectedItemToSchedule === 'SPECIALIST') {
        if (specialistUnitSelected) return true;
      }
      if (
        selectedItemToSchedule === 'PRODUCT' ||
        selectedItemToSchedule === 'PRODUCT_ENV' ||
        selectedItemToSchedule === 'SERVICE_ENV'
      ) {
        if (careType === typeLocationSchedule.TELEMEDICINE) {
          if (selectedUnit) return true;
          return false;
        }
        if (
          careType === typeLocationSchedule.PRESENTIAL ||
          careType === typeLocationSchedule.PROCEDURE ||
          careType === typeLocationSchedule.DRIVE_THRU
        ) {
          if (selectedUnit) return true;
          return false;
        }
        if (careType === typeLocationSchedule.EINSTEIN_TO_YOU) {
          if (eavLocation) return true;
          return false;
        }
      }
      return false;
    };

    const handleSubmitScheduleType = async () => {
      setLoadingChangeProduct(true);
      if (
        selectedItemToSchedule === 'PRODUCT' ||
        selectedItemToSchedule === 'PRODUCT_ENV' ||
        selectedItemToSchedule === 'SERVICE_ENV'
      )
        if (coverageUnit && selectedItem) {
          if (coverageUnit.coverage?.care_type === 'EINSTEIN_TO_YOU') {
            try {
              if (patient) {
                const { schedule } = getNotScheduledProducts({
                  patient,
                  productList
                })[0];

                if (schedule) {
                  const product = findProduct(schedule.productId);
                  const coverage = product?.coverages?.find(({ care_type }) => care_type === 'EINSTEIN_TO_YOU');

                  const { cep, logradouro, cidade, uf, numero, complemento, pontoReferencia } = eavLocation;

                  const findRuleSalesforceId = coverageUnit.coverage.rules.find(
                    rl => rl.ruleId === process.env.REACT_APP_SALES_FORCE_RULE_VALUE_ID
                  );
                  const findRuleSalesforceName = coverageUnit.coverage.rules.find(
                    rl => rl.ruleId === process.env.REACT_APP_SALES_FORCE_RULE_NAME_ID
                  );

                  if (!patient) {
                    toast.error('Paciente não encontrado!');
                    return;
                  }

                  if (!findRuleSalesforceId || !findRuleSalesforceName) {
                    toast.error('Regra do SalesForce não encontrada!');
                    return;
                  }

                  const { document, documentType, birthDate, email, gender, name, phone } = patient;

                  const splitName = name.split(' ');
                  const checkSlotsObj = {
                    patient: {
                      document,
                      documentType,
                      birthDate,
                      firstName: splitName.slice(0, -1).join(' '),
                      lastName: splitName.slice(-1).join(' '),
                      sex: gender === 'Masculino' ? 'M' : 'F',
                      phoneNumber: phone,
                      email,
                      address: {
                        street: `${logradouro}`,
                        city: cidade,
                        state: uf,
                        country: 'Brasil',
                        postalCode: cep,
                        number: numero,
                        complement: complemento,
                        ...(pontoReferencia ? { referencePoint: pontoReferencia } : {})
                      }
                    },
                    schedules: [
                      {
                        itemId: coverage?.item_SGH_id ?? '',
                        itemName: findRuleSalesforceName.ruleValue ?? '',
                        salesForceId: findRuleSalesforceId.ruleValue ?? '',
                        productId: schedule.id ?? '',
                        productName: schedule.productName ?? '',
                        careType: ATTENDANCE_TYPE['EINSTEIN_TO_YOU'],
                        appointmentType: APPOINTMENT_TYPE['IMMUNIZATION'],
                        catalogItemId: coverage?.id ?? ''
                      }
                    ],
                    payment: {
                      type: 1,
                      price: Number(product?.price)
                    }
                  };
                  const eavSlotsRes = await checkSalesForceSlots(checkSlotsObj);

                  if (eavSlotsRes) {
                    setEavSlotsData(eavSlotsRes);
                  } else return;
                }
              }
            } catch (err) {
              console.log('err', err);
            } finally {
              setLoadingChangeProduct(false);
            }
          }
          const scheduleProps = useScheduling.schedule;

          if (selectedCoverage?.channel === ScheduleChannelsMapped.TELEORIENTATION) {
            scheduleProps.catalogItemId = coverageUnit.coverage?.id;
            scheduleProps.productId = selectedItem.productId;
            scheduleProps.itemId = ScheduleChannelsMapped.TELEORIENTATION;
            scheduleProps.unit = {} as UnitAppointment;
            scheduleProps.unitId = ScheduleChannelsMapped.TELEORIENTATION;
          }

          const units = selectedCoverage?.units;
          const selectedUnit = units?.[0];

          if (units?.length === 1 && selectedUnit) {
            const location = selectedUnit?.locations?.[0]?.name;
            const localId = selectedUnit?.local_id ?? location;

            useScheduling.addUnit({
              unit: selectedUnit,
              localId: localId ?? ''
            });
          }

          useScheduling.addChannel(selectedCoverage?.channel || '');
          useScheduling.addCareType(selectedCoverage?.care_type || '');
          useScheduling.updateSchedule();
          gtmEvent({
            event: 'agendamento-einstein',
            categoria: useScheduling.schedule?.productName?.includes('gripe') ? 'Agendar-Vacina' : 'Agendar-Cuidado',
            acao: 'step-4',
            rotulo: 'tipo-de-atendimento',
            atendimento: selectedCoverage?.care_type //exemplo: presencial ou drive-thru
          });

          goTo(PAGES.SERVICE_UNIT.id);
        }
      if (selectedItemToSchedule === 'SPECIALIST') {
        if (specialistUnitSelected) {
          const scheduleSpecialist = ScheduleSpecialistBuilder(scheduleSpecialistSelected);

          try {
            const companyId = Number(specialistUnitSelected.external_platform.code_company);
            const medicalProcedureId = selectedCoverageSpecialist.presencial ? -4 : -5;

            const { today, endDate } = getRangeDateFromToday(30);

            const dateHoursResult = await fetchSlotsSpecialistDates({
              professionalId: Number(specialistUnitSelected.external_platform.code_professional),
              companyId: companyId,
              medicalProcedureIds: medicalProcedureId,
              startDate: format(today, 'yyyy-MM-dd'),
              endDate: format(endDate, 'yyyy-MM-dd'),
              specialistId: selectedSpecialist?.id || '',
              officeId: specialistUnitSelected.office_id,
              attendanceType: selectedCoverageSpecialist.presencial ? 'PRESENTIAL' : 'TELEMEDICINE',
              appointmentType: scheduleSpecialistSelected.appointmentType || '',
              findThreeDates: true
            });

            if (dateHoursResult.error) {
              toast.error('Não foi possível consultar agenda.');
            }
            if (!dateHoursResult.error) {
              if (dateHoursResult.data.length === 0) toast.error('Esse especialista não possui agenda disponível.');

              if (dateHoursResult.data.length !== 0) {
                scheduleSpecialist.setResource({
                  companyId: companyId,
                  medicalProcedureId: medicalProcedureId,
                  professionalId: specialistUnitSelected.external_platform.code_professional,
                  crm: selectedSpecialist?.professional_codes[0].value || '',
                  resourceName: selectedSpecialist?.name || '',
                  specialistId: selectedSpecialist?.id || '',
                  specialty: handleSpecialtiesWithExpertises(selectedSpecialist?.specialties || [])
                });

                scheduleSpecialist.setUnit({
                  // return_time,
                  attendanceType: selectedCoverageSpecialist.presencial ? 'PRESENTIAL' : 'TELEMEDICINE',
                  office_id: specialistUnitSelected.office_id,
                  unitName: specialistUnitSelected.name,
                  // unitHealthPlans: filteredUnitHealthPlans,
                  catalogUnitId: specialistUnitSelected.id,
                  unitAddress: specialistUnitSelected.full_address
                });

                goTo(PAGES.PAYMENT_VIEW.id);
              }
            }
          } catch (error) {
            console.error(error);
            toast.error('Não foi possível consultar agenda.');
          }
        }
      }
      setLoadingChangeProduct(false);
    };

    const handleSubmitCheckup = () => {
      goTo(PAGES.SERVICE_UNIT_MULTI.id);
    };

    React.useEffect(() => {
      if (selectedCoverage?.channel === ScheduleChannelsMapped.TELEORIENTATION) handleSubmitScheduleType();
      if (scheduleItems.length && selectedService)
        if (process.env.REACT_APP_CHECKUP_PRODUCT_ID === selectedService.id) handleSubmitCheckup();
    }, [selectedCoverage]);

    return (
      <Button
        ref={ref}
        title="Continuar"
        onClick={handleSubmitScheduleType}
        disabled={!verifyAllowNextStep(selectedCoverage?.care_type)}
      />
    );
  }
);
